import React, { useState, useEffect, useRef } from "react";
import { Button, IconButton, Snackbar, Alert, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { BottomArrowIcon, CopyIcon } from "../../../components/Icons";
import { regenerateBackupCodes } from "../../../store/actions/authActions";
import { images } from "../../../assets";
import { generateBackupCodesPDF } from "./pdfBackupCodes";
import {
  SaveBackupCodesContainer,
  SaveBackupCodesIcon,
  SaveBackupCodesTitle,
  SaveBackupCodesSubtitle,
  SaveBackupCodesList,
  SaveBackupCodesCode,
  SaveBackupCodesActions,
  SaveBackupCodesCollapseAllContainer,
  SaveBackupCodes,
} from "./SaveBackupCodesPage.styled";
import theme from "../../../theme/theme";

const SaveBackupCodesPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedCodes = useRef(false);

  // Get backup codes from regeneration
  useEffect(() => {
    const getBackupCodes = async () => {
      // Only fetch codes if we haven't already fetched them
      if (!hasFetchedCodes.current) {
        try {
          hasFetchedCodes.current = true;
          setIsLoading(true);
          const codes = await dispatch(regenerateBackupCodes()).unwrap();
          setBackupCodes(codes.codes);
        } catch (error) {
          hasFetchedCodes.current = false; // Reset on error to allow retry
          setBackupCodes([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    getBackupCodes();
  }, [dispatch]);

  const handleCopyAll = async () => {
    try {
      const codesText = backupCodes.join("\n");
      await navigator.clipboard.writeText(codesText);
      setShowSnackbar(true);
    } catch (error) {
      console.error("Failed to copy codes:", error);
    }
  };

  const handleDownloadPDF = () => {
    generateBackupCodesPDF({
      codes: backupCodes,
      subtitle: t("saveBackupCodes.subtitle"),
      filename: `backup-codes-${new Date().toISOString().split("T")[0]}.pdf`,
    });
  };

  const handleContinue = () => {
    navigate("/save-backup-codes-success");
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (isLoading) {
    return (
      <AuthPageLayout contentWidth="817px">
        <SaveBackupCodesContainer>
          <SaveBackupCodesIcon>
            <Box
              component="img"
              src={images.diamond}
              alt="Diamond icon"
              sx={{ width: "136px" }}
            />
          </SaveBackupCodesIcon>

          <SaveBackupCodesTitle>
            {t("saveBackupCodes.title")}
          </SaveBackupCodesTitle>

          <SaveBackupCodesSubtitle>
            {t("saveBackupCodes.generating")}
          </SaveBackupCodesSubtitle>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
              fontSize: "16px",
              color: "#666",
            }}
          >
            Loading...
          </Box>
        </SaveBackupCodesContainer>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout contentWidth="817px">
      <SaveBackupCodesContainer>
        <SaveBackupCodesIcon>
          <Box
            component="img"
            src={images.diamond}
            alt="Diamond icon"
            sx={{ width: "136px" }}
          />
        </SaveBackupCodesIcon>

        <SaveBackupCodesTitle>
          {t("saveBackupCodes.title")}
        </SaveBackupCodesTitle>

        <SaveBackupCodesSubtitle>
          {t("saveBackupCodes.subtitle")}
        </SaveBackupCodesSubtitle>

        <SaveBackupCodes>
          {!isCollapsed && (
            <SaveBackupCodesList>
              {backupCodes.map((code, index) => (
                <SaveBackupCodesCode key={index}>{code}</SaveBackupCodesCode>
              ))}
            </SaveBackupCodesList>
          )}
          <SaveBackupCodesCollapseAllContainer>
            <p onClick={handleToggleCollapse}>
              {isCollapsed ? "See All Codes" : "Collapse All Codes"}
              <BottomArrowIcon
                style={{
                  transform: !isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
                }}
                fill={theme.palette.primary.main}
              />
            </p>

            <IconButton onClick={handleCopyAll} size="small">
              <CopyIcon width={24} height={24} />
            </IconButton>
          </SaveBackupCodesCollapseAllContainer>
        </SaveBackupCodes>

        <SaveBackupCodesActions>
          <Button variant="outlined" onClick={handleDownloadPDF}>
            {t("saveBackupCodes.downloadPDF")}
          </Button>

          <Button variant="contained" onClick={handleContinue}>
            {t("saveBackupCodes.continue")}
          </Button>
        </SaveBackupCodesActions>
      </SaveBackupCodesContainer>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("saveBackupCodes.copiedToClipboard")}
        </Alert>
      </Snackbar>
    </AuthPageLayout>
  );
};

export default SaveBackupCodesPage;
